// WeekPicker — combobox for the Workforce Planning header.
//
// Replaces a plain <Input value="2026-W18"> with a dropdown listing
// every ISO week that has data (schedule / presence / archive) plus
// the current week and the next 12 forward weeks. Each item shows the
// date range and a small badge for what data it carries, so the user
// can spot weeks 17/18 instantly when planning week 19.
//
// The user can still type a free-form `YYYY-Www` value (Enter to
// commit) — useful for jumping to a week that never had data. The
// "data" weeks group is sorted desc so most recent is on top.
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Check, Database, Archive, ClipboardCheck } from 'lucide-react';
import { api } from './_helpers';

const fmtBounds = (mon, sun) => {
  if (!mon || !sun) return '';
  const f = (s) => {
    const [y, m, d] = s.split('-');
    return `${d}.${m}`;
  };
  return `${f(mon)} → ${f(sun)}`;
};

export const WeekPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const [draft, setDraft] = useState(value || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => { setDraft(value || ''); }, [value]);

  // Re-fetch the catalogue every time the popover opens so the user
  // sees fresh weeks even if a peer just added data in another tab.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api('/planning/weeks-with-data')
      .then(d => { if (!cancelled) setWeeks(d?.weeks || []); })
      .catch(() => { if (!cancelled) setWeeks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const groups = useMemo(() => {
    const dataWks = weeks.filter(w => w.has_schedule || w.has_presence || w.has_archive);
    const future = weeks.filter(w => !(w.has_schedule || w.has_presence || w.has_archive));
    return { dataWks, future };
  }, [weeks]);

  const commitFreeform = () => {
    const v = (draft || '').trim();
    if (!v) return;
    // Match backend's stricter 1..53 range to avoid switching to weeks
    // the backend will silently treat as empty (e.g. 2026-W99 / W00).
    const m = /^(\d{4})-W(\d{2})$/.exec(v);
    if (!m) return;
    const w = Number(m[2]);
    if (w < 1 || w > 53) return;
    onChange?.(v);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-2 h-7 px-2 font-mono text-xs justify-between gap-1 min-w-[110px]"
          data-testid="week-picker-trigger"
        >
          <span>{value || '—'}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start" data-testid="week-picker-popover">
        {/* Free-form input for arbitrary weeks */}
        <div className="p-2 border-b flex items-center gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && commitFreeform()}
            placeholder="YYYY-Www"
            className="h-7 font-mono text-xs"
            data-testid="week-picker-input"
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-7 text-xs"
            onClick={commitFreeform}
            data-testid="week-picker-go"
          >Go</Button>
        </div>

        <div className="max-h-[320px] overflow-y-auto py-1 text-sm">
          {loading && <p className="px-3 py-2 text-xs text-muted-foreground">Loading weeks…</p>}

          {!loading && groups.dataWks.length > 0 && (
            <>
              <p className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                Weeks with data
              </p>
              {groups.dataWks.map(w => (
                <button
                  key={w.week_id}
                  type="button"
                  onClick={() => { onChange?.(w.week_id); setOpen(false); }}
                  className={`w-full px-3 py-1.5 flex items-center justify-between gap-2 hover:bg-muted/50 text-left ${value === w.week_id ? 'bg-emerald-50' : ''}`}
                  data-testid={`week-picker-option-${w.week_id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {value === w.week_id
                      ? <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      : <span className="w-3.5 shrink-0" />}
                    <span className="font-mono text-xs">{w.week_id}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{fmtBounds(w.monday, w.sunday)}</span>
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    {w.has_schedule && (
                      <span title={`${w.assignment_count} shift assignments`} className="inline-flex items-center gap-0.5 px-1 rounded bg-sky-100 text-sky-700 text-[10px]">
                        <Database className="h-2.5 w-2.5" />{w.assignment_count}
                      </span>
                    )}
                    {w.has_presence && (
                      <span title="Presence sheet" className="inline-flex items-center px-1 rounded bg-amber-100 text-amber-700 text-[10px]">
                        <ClipboardCheck className="h-2.5 w-2.5" />
                      </span>
                    )}
                    {w.has_archive && (
                      <span title="Archived snapshot" className="inline-flex items-center px-1 rounded bg-violet-100 text-violet-700 text-[10px]">
                        <Archive className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </>
          )}

          {!loading && groups.future.length > 0 && (
            <>
              <p className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                Upcoming weeks (no data yet)
              </p>
              {groups.future.map(w => (
                <button
                  key={w.week_id}
                  type="button"
                  onClick={() => { onChange?.(w.week_id); setOpen(false); }}
                  className={`w-full px-3 py-1.5 flex items-center justify-between gap-2 hover:bg-muted/50 text-left ${value === w.week_id ? 'bg-emerald-50' : ''}`}
                  data-testid={`week-picker-option-${w.week_id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {value === w.week_id
                      ? <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      : <span className="w-3.5 shrink-0" />}
                    <span className="font-mono text-xs">{w.week_id}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{fmtBounds(w.monday, w.sunday)}</span>
                  </span>
                </button>
              ))}
            </>
          )}

          {!loading && weeks.length === 0 && (
            <p className="px-3 py-3 text-xs text-muted-foreground">No weeks available yet.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
